/* eslint-disable key-spacing */
/* eslint-disable no-multi-spaces */
import React, { useContext, Fragment, useEffect } from 'react'
import { Field, useField, useFormikContext } from 'formik'
import Box from '@material-ui/core/Box'
import round from 'lodash/round'
import upperCase from 'lodash/upperCase'
import get from 'lodash/get'

import { SliderInlineFieldWithLabels } from '@components/Form/Slider'
import { InputField, InputFieldWithLabel, NumberFieldWithLabel, DateFieldWithLabel, SelectWithLabel, FieldError, CheckboxWithLabel, ToggleFieldWithLabel } from '@components/Form/Fields'
import { Hider } from '@components/HOCs/Hider'
import { AssetCard } from '@components/Assets/card'
import { getYearFromAge } from '@utils/dateParser'
import useFeatureConfig, { FeatureNames } from '@hooks/useFeatureConfig'
import { R } from '@utils/currency'

import { AssetsContext } from '../../context'
import { getLabel, getFieldsToTouch } from './formService'
import {
  ANNUAL_INCREASE_MAX,
  ANNUAL_INCREASE_MIN,
  planInflationRate,
  shouldUntickPlanInflation
} from './annualIncrease'
import { AssetAllocationGroup } from './AssetAllocationGroup'
import { ASIDE_VIEWS } from '.'
import { AssetFieldGroupsNames } from '../../utils/formFieldsForAssetType'
import './asset-fieldset.scss'

export const INTEREST_RATE_CONTROL = Object.freeze({
  BASED_ON_RISK_PROFILE: 1,
  BASED_ON_SPECIFIED_INTEREST_RATE: 2,
  BASED_ON_ASSET_ALLOCATION: 3,
  BASED_ON_ASSET_PORTFOLIO: 4
})

const MAX_SAVINGS_POT_SEED_VALUE = 30000

export const AssetFields = ({ values, errors, setFieldValue, setValues, setFieldTouched, setAsideView }) => {
  const { state } = useContext(AssetsContext)
  const { groupedFormFields, formAssetType, scenario, assumptions } = state
  const { isRetired } = scenario

  return (
    Object.keys(groupedFormFields).map((groupID, index) => {
      const formFields = groupedFormFields[groupID]
      const groupLabel = AssetFieldGroupsNames[groupID]
      if (!formFields || formFields.length === 0) return null

      return (
        <fieldset key={index} className="asset-fieldset">
          <legend>{groupLabel}</legend>

          {formFields.map((field) =>
            <AssetField
              key={field}
              name={field}
              label={getLabel(field, formAssetType, assumptions, isRetired)}
              values={values}
              errors={errors}
              setFieldValue={setFieldValue}
              setValues={setValues}
              setFieldTouched={setFieldTouched}
              setAsideView={setAsideView}
            />
          )}
        </fieldset>
      )
    })
  )
}

export const AssetField = ({ name, label, values, errors, setFieldValue, setValues, setFieldTouched, setAsideView }) => {
  const { state, } = useContext(AssetsContext)
  const { formAssetType, userDetails } = state
  const isWealthManager = userDetails?.isWealthManager

  const lastImportedAt = state.asset.last_automatic_import_at
  const [{ isEnabled: isAutoLinkingEnabled }, { isEnabled: isAssetEventsEnabled }] = useFeatureConfig(
    FeatureNames.ASSET__AUTO_LINKING,
    FeatureNames.ASSET_EVENT__ALL
  )
  if (name === 'name') {
    return <InputFieldWithLabel name={name} label={label} />
  } else if (name === 'contractNumber') {
    return isAutoLinkingEnabled()
      ? <InputFieldWithLabel name={name} label={label} subLabel={"The contract number cannot be changed once the asset starts receiving data."} readOnly={!!lastImportedAt} value={values.contractNumber} />
      : null
  } else if (name === 'assetCurrency') {
    return <CurrencyField name={name} values={values} label={label} />
  } else if (name === 'interestRate') {
    if (formAssetType !== 'investment_property' && formAssetType !== 'other_income') {
      return <InterestRateFields name={name} setFieldValue={setFieldValue} values={values} errors={errors} />
    } else {
      return (
        <NumberFieldWithLabel
          name={name}
          value={values[name]}
          label={label}
          touchFields={getFieldsToTouch(name)}
          placeholder={0}
          precision={1}
        />
      )
    }
  } else if (name === 'serviceProviderId') {
    if (isAutoLinkingEnabled()) {
      return (
        <Box m='1rem'>
          <SelectWithLabel
            className='app-form__select--service-provider'
            name='serviceProviderId'
            options={state.serviceProviders}
            label='Service Provider'
            subLabel={"The service provider cannot be changed once the asset starts receiving data."}
            disabled={!!lastImportedAt}
          />
        </Box>
      )
    } else {
      return null
    }
  } else if (name == 'annualIncreaseInMonthlyContribution') {
    return (values.monthlyContribution || values.employerMonthlyContribution)
      ? <AnnualIncreaseFields name={name} label={label} values={values} setFieldValue={setFieldValue} />
      : null
  } else if (name === 'changeGrossIncomeEvent') {
    return isAssetEventsEnabled()
      ? <ChangeGrossIncomeEventFields name={name} values={values} label={label} setFieldValue={setFieldValue} />
      : null
  } else if (name === 'changeMonthlyContributionEvent') {
    return isAssetEventsEnabled()
      ? <ChangeMonthlyContributionEventFields name={name} values={values} label={label} setFieldValue={setFieldValue} />
      : null
  } else if (name === 'matureRetirementAssetEvent') {
    return isAssetEventsEnabled()
      ? <MatureRetirementAssetEventFields name={name} label={label} setAsideView={setAsideView} />
      : null
  } else if (name === 'reviewDate') {
    if (isWealthManager) {
      return (
        <DateFieldWithLabel
          label={label}
          name={name}
          values={values}
          errors={errors}
          touched={getFieldsToTouch(name)}
          setFieldValue={setFieldValue}
          setValues={setValues}
          setFieldTouched={setFieldTouched}
          dateFormat='MMMM do'
        />
      )
    } else {
      return null
    }
  } else if (name === 'allowAutomatedIncomeCalculation') {
    return values.monthlyIncome ? (
      <CheckboxWithLabel
        name={name}
        label={label} />
    ) : null
  } else if (name === 'monthlyIncome' && formAssetType === 'living_annuity') {
    return (
      <NumberFieldWithLabel
        name={name}
        value={values[name]}
        subtext={
          R(values.gross).value > 0
            ? `The monthly income is expected to be between 2.5% and 17.5% of the total value of the asset.\n
             (${R(R(values.gross).value * 0.025, 2).format()} - ${R(R(values.gross).value * 0.175, 2).format()})`
            : null
        }
        label={label}
        touchFields={getFieldsToTouch(name)}
        placeholder={0}
      />
    )
  } else if (name == "monthlyProfit" && formAssetType == "investment_property") {
    return (
      <NumberFieldWithLabel
        name={name}
        value={values[name]}
        label={label}
        touchFields={getFieldsToTouch(name)}
        placeholder={0}
        numberFormatProps={{
          allowNegative: true
        }}
      />
    )
  } else if (name === 'potBreakdown') {
    return (
      <PotBreakdownFields
        name={name}
        values={values}
        setFieldValue={setFieldValue}
      />)
  } else if (name === 'gross' && values.composition === 'two_pot') {
    return (
      <TwoPotGrossValueField
        name={name}
        values={values}
        label={label}
        setFieldValue={setFieldValue}
      />)
  } else if (name === 'savingsPotAnnualWithdrawal') {
    return (
      <Hider withTransition collapse
        show={values.composition === 'two_pot'}
        render={() => {
          return <div>
            <NumberFieldWithLabel
              name={`potBreakdown.savingsPot.annual_income`}
              value={values['potBreakdown']['savingsPot']['annual_income']}
              label={'Annual withdrawal from savings pot'}
              placeholder={0}
            />
          </div>
        }}
      />
    )
  } else {
    return (
      <NumberFieldWithLabel
        name={name}
        value={values[name]}
        label={label}
        touchFields={getFieldsToTouch(name)}
        placeholder={0}
      />
    )
  }
}

export const CurrencyField = ({ name, values, label }) => {
  const { state } = useContext(AssetsContext)
  const { supportedCurrencies, assumptions } = state
  const { offshoreInflationRates, inflation } = assumptions

  return (
    <div className='app-form__field asset-form__field'>
      <label>{label}</label>
      <OffshoreInflationSublabel
        currency={values[name]}
        inflation={inflation}
        offshoreInflationRates={offshoreInflationRates}
      />
      <div className='currency-input'>
        {Object.keys(supportedCurrencies).map((currency) =>
          <Fragment key={currency}>
            <label className='currency-label'>
              <Field key={currency} name={name} type='radio' value={currency} checked={values[name] === currency} />
              <span className='radio-button' />
              {currency.toUpperCase()}
            </label>
          </Fragment>
        )}
      </div>
    </div>
  )
}

const OffshoreInflationSublabel = ({ currency, offshoreInflationRates, inflation }) => {
  const currencyInflation = round(offshoreInflationRates[currency] * 100, 2)
  return (
    <small className='sublabel'>
      <b>{upperCase(currency)}</b> inflation: <b>{currencyInflation}%</b> |
      Client inflation: <b> {round(inflation * 100, 2)}%</b> |
      Client currency depreciation: <b> {round((inflation * 100) - currencyInflation, 2)}%</b>
    </small>
  )
}

// The asset's annual increase either tracks the plan's inflation rate or holds its own
// rate. Both controls stay on screen: while it is tracking, the slider shows the plan's
// rate greyed out, and moving it is what switches the asset to its own rate.
const AnnualIncreaseFields = ({ name, label, values, setFieldValue }) => {
  const { state } = useContext(AssetsContext)
  const { assumptions } = state
  const planRate = planInflationRate(assumptions)
  const followsPlan = values.usePlanInflation

  // Pins the slider to the plan's rate, including when an advisor edits the plan's
  // inflation while this form is open.
  useEffect(() => {
    if (followsPlan && values[name] !== planRate) {
      setFieldValue(name, planRate)
    }
  }, [followsPlan, planRate])

  // Moving the slider off the plan's rate means the advisor wants their own rate.
  useEffect(() => {
    if (followsPlan && shouldUntickPlanInflation(values[name], planRate)) {
      setFieldValue('usePlanInflation', false)
    }
  }, [values[name]])

  return (
    <>
      <CheckboxWithLabel
        name='usePlanInflation'
        label={`Match the plan's inflation rate (${planRate}%)`} />

      <div className={followsPlan ? 'opacity-50' : ''}>
        <SliderInlineFieldWithLabels
          title={label}
          name={name}
          values={values}
          disabled={followsPlan}
          min={ANNUAL_INCREASE_MIN}
          max={ANNUAL_INCREASE_MAX}
          step={0.5}
        />
      </div>
    </>
  )
}

const InterestRateFields = (props) => {
  const { name, values, errors } = props
  const { state } = useContext(AssetsContext)
  const { formAssetType, isExistingAsset } = state
  return (
    <>
      <Box
        display='flex'
        justifyContent='space-between'
        m='1rem'
      >
        <SelectWithLabel
          subLabel={getSublabelIfOffshore(formAssetType)}
          className='app-form__select--return-rate'
          name='interestRateControl'
          options={interestRateControlOptions(formAssetType, isExistingAsset)}
          label='Annual interest rate %'
        />
        <InputField withoutError className='app-form__field--return-rate' disabled={values.interestRateControl !== INTEREST_RATE_CONTROL.BASED_ON_SPECIFIED_INTEREST_RATE} name={name} />
      </Box>
      <FieldError name={name} />
      <AssetAllocationGroup values={values} errors={errors} />
    </>
  )
}

// The ChangeGrossIncomeEvent has an internal 'active'
// which will be set to the inverse of the '_destroy' field.
const ChangeGrossIncomeEventFields = (props) => {
  const { name, label, values, setFieldValue } = props
  const { [name]: {
    active,
  } } = values

  // _destroy is set to the inverse of active
  useEffect(() => {
    setFieldValue(`${name}._destroy`, !active)
  }, [active])

  return (
    <Box className='mb-4'>
      <CheckboxWithLabel
        name={`${name}.active`}
        label={label}
      />
      <Box ml='0.5rem'>
        <Hider withTransition collapse
          show={!values.changeGrossIncomeEvent._destroy}
          render={() => {
            return <div>
              <NumberFieldWithLabel
                name={`${name}.payload.value`}
                label={'Total monthly withdrawal at retirement'} />
              <CheckboxWithLabel
                name={`${name}.payload.metadata.allowCalculationToExceedSpecifiedValue`}
                label={'Allow this asset to withdraw more than specified to cover budget shortfall'} />
            </div>
          }}
        />
      </Box>
    </Box>
  )
}

// The ChangeGrossIncomeEvent has an internal 'active'
// which will be set to the inverse of the '_destroy' field.
const ChangeMonthlyContributionEventFields = (props) => {
  const { name, label, values, setFieldValue } = props
  const { [name]: {
    active,
  } } = values

  // _destroy is set to the inverse of active
  useEffect(() => {
    setFieldValue(`${name}._destroy`, !active)
  }, [active])

  return (
    <Box className='mb-4'>
      <CheckboxWithLabel
        name={`${name}.active`}
        label={label}
      />
      <Box ml='0.5rem'>
        <Hider withTransition collapse
          show={!values.changeMonthlyContributionEvent._destroy}
          render={() => <NumberFieldWithLabel name={`${name}.payload.value`} label={'Monthly contribution at retirement'} />}
        />
      </Box>
    </Box>
  )
}

const TwoPotGrossValueField = ({ name, values, label, setFieldValue }) => {
  const { state } = useContext(AssetsContext)
  const { asset } = state
  const newAsset = !asset?.id
  // The pot gross value fields need to react when this is changed
  const onChange = (e) => {
    const { value } = e.target
    setFieldValue('gross', R(value).value)

    const savingsPotGrossValue = Math.min(0.1 * R(value).value, MAX_SAVINGS_POT_SEED_VALUE)
    const vestedPotGrossValue = R(value).value - savingsPotGrossValue

    setFieldValue(`potBreakdown.savingsPot.gross_value`, savingsPotGrossValue)
    setFieldValue(`potBreakdown.vestedPot.gross_value`, vestedPotGrossValue)
    setFieldValue(`potBreakdown.retirementPot.gross_value`, 0)
  }

  return (
    <NumberFieldWithLabel
      name={name}
      value={values[name]}
      onChange={onChange}
      disabled={!newAsset}
      subtext={
        newAsset &&
        'Changing the gross value will automatically update the pot breakdown to initial values'
      }
      label={label}
      touchFields={getFieldsToTouch(name)}
      placeholder={0}
    />
  )
}

const PotBreakdownFields = ({ name, setFieldValue, values }) => {
  const { state } = useContext(AssetsContext)
  const { asset, userDetails } = state
  const { twoPotOptional } = userDetails
  const newAsset = !asset?.id
  const twoPotReady = asset?.twoPotReady

  const linked = asset?.linked
  const serviceProvider = asset?.serviceProvider?.name || 'the asset provider'

  const {
    [name]: {
      isTwoPot,
      vestedPot: { gross_value: vestedPotGrossValue },
      savingsPot: { gross_value: savingsPotGrossValue },
      retirementPot: { gross_value: retirementPotGrossValue }
    },
    gross
  } = values


  // The total gross value fields need to react when this is changed
  const onChange = (potName) => (e) => {
    const { value } = e.target
    setFieldValue(`potBreakdown.${potName}.gross_value`, R(value).value)

    const potGrossValues = {
      vestedPot: R(vestedPotGrossValue).value,
      savingsPot: R(savingsPotGrossValue).value,
      retirementPot: R(retirementPotGrossValue).value,
      ...{
        [potName]: R(value).value
      }
    }


    const totalGrossValue = potGrossValues.vestedPot + potGrossValues.savingsPot + potGrossValues.retirementPot
    setFieldValue(`gross`, totalGrossValue)
  }

  // Toggle the hider if the isTwoPot value changes
  useEffect(() => {
    setFieldValue(`composition`, isTwoPot ? 'two_pot' : 'standalone')

    if (isTwoPot && R(gross).value > 0 && R(vestedPotGrossValue).value <= 0) {
      const savingsPotGrossValue = Math.min(0.1 * R(gross).value, MAX_SAVINGS_POT_SEED_VALUE)
      const vestedPotGrossValue = R(gross).value - savingsPotGrossValue

      setFieldValue(`potBreakdown.savingsPot.gross_value`, savingsPotGrossValue)
      setFieldValue(`potBreakdown.vestedPot.gross_value`, vestedPotGrossValue)
      setFieldValue(`potBreakdown.retirementPot.gross_value`, 0)
    }
  }, [isTwoPot])

  return (
    <Box>
      {twoPotOptional &&
        <CheckboxWithLabel
          name={`${name}.isTwoPot`}
          label={'Two-pot retirement fund'}
        />
      }
      {
        twoPotReady || newAsset || twoPotOptional
          ? <Box mx='1rem'>
            <Hider withTransition collapse
              show={values.composition === 'two_pot'}
              render={() =>
                <>
                  {!newAsset
                    ?
                    <small className='sublabel'>
                      👋 We’re exploring options to bring more control over the two pot breakdown, such as adjusting interest rates per pot, but we’re still waiting for more data from different providers before we finalize direction.
                      If you have feedback for us, let us know via our
                      <a style={{
                        letterSpacing: 'unset',
                        textTransform: 'unset',
                        fontWeight: 'normal'
                      }} onClick={() => Intercom("showNewMessage")}> support chat.</a>
                    </small>
                    : null
                  }
                  <NumberFieldWithLabel
                    name={`${name}.vestedPot.gross_value`}
                    value={values[name].vestedPot.gross_value}
                    onChange={onChange('vestedPot')}
                    label={'Vested pot value'}
                    placeholder={0}
                  />
                  <NumberFieldWithLabel
                    name={`${name}.savingsPot.gross_value`}
                    value={values[name].savingsPot.gross_value}
                    onChange={onChange('savingsPot')}
                    label={'Savings pot value'}
                    placeholder={0}
                  />
                  <NumberFieldWithLabel
                    name={`${name}.retirementPot.gross_value`}
                    subtext={'Changing any of the pot values will set the total gross value of the asset to them sum of the pot values'}
                    value={values[name].retirementPot.gross_value}
                    onChange={onChange('retirementPot')}
                    label={'Retirement pot value'}
                    placeholder={0}
                  />
                </>
              }
            />
          </Box>
          :
          values.composition === 'two_pot' && linked
            ? <Box mx='1rem'>
              <small className='sublabel'>
                👋 We're still waiting on updated data from {serviceProvider} to bring you the two pot asset breakdown. Not all providers will support supplying a breakdown via their data feeds. Providers gave us a timeline of 1 - 4 months for their feeds to be updated.
                If you have feedback for us, let us know via our
                <a style={{
                  letterSpacing: 'unset',
                  textTransform: 'unset',
                  fontWeight: 'normal'
                }} onClick={() => Intercom("showNewMessage")}> support chat.</a>
              </small>
            </Box>
            : null

      }
    </Box>
  )
}

const MatureRetirementAssetEventFields = (props) => {
  const { state } = useContext(AssetsContext)
  const { assets, supportedCurrencies, userDetails, scenario } = state
  const { clientCurrentAge } = userDetails
  const retirementAge = scenario?.retirementAge

  const { name, label, setAsideView } = props

  const { values, errors: { [name]: eventErrors }, setFieldValue } = useFormikContext()
  const ageErrors = eventErrors && eventErrors.when && eventErrors.when.value
  const { [name]: { when: { value: ageValue } } } = values
  const {
    [name]: {
      active,
      payload: {
        unit_trust: {
          recipient_id: unit_trust_recipient_id
        },
        living_annuity: {
          recipient_id: living_annuity_recipient_id
        }
      }
    }
  } = values

  // _destroy is set to the inverse of active, or if both recipient_id's are default
  useEffect(() => {
    if (!active || (unit_trust_recipient_id === 'default' && living_annuity_recipient_id === 'default')) {
      setFieldValue(`${name}._destroy`, true)
    } else {
      setFieldValue(`${name}._destroy`, false)
    }
  }, [unit_trust_recipient_id, living_annuity_recipient_id, active])

  const selected_unit_trust = assets.find((asset) => asset.id === unit_trust_recipient_id)
  const selected_living_annuity = assets.find((asset) => asset.id === living_annuity_recipient_id)

  return (
    <Box
      className='mb-4'
    >
      <CheckboxWithLabel
        name={`${name}.active`}
        label={label}
      />
      <Hider withTransition collapse
        show={values.matureRetirementAssetEvent.active}
        render={() =>
          <Box m='1rem'>
            <NumberFieldWithLabel
              name={`${name}.when.value`}
              label={'At what age will this asset mature'}
              subtext={!ageErrors ? eventAgeSubtext(ageValue, clientCurrentAge, retirementAge) : null}
            />
            <Box className='m-4'>
              <SelectWithLabel name={`${name}.payload.unit_trust.recipient_id`} label='Lump sum'
                subLabel={'Up to the first R550 000 can either be withdrawn or transfered to a Unit trust'}
                options={getUnitTrustMaturityOptions(assets)}
              />
            </Box>
            {unit_trust_recipient_id === 'default-retirement-unit-trust' &&
              <small style={{ marginLeft: "1rem" }}>
                <a onClick={() => setAsideView(ASIDE_VIEWS.DEFAULT_UNIT_TRUST_EXPLANATION)}>
                  What is the Default Unit Trust?
                </a>
              </small>
            }
            {unit_trust_recipient_id === 'cash-withdrawal' &&
              <small style={{ marginLeft: "1rem" }}>
                <a onClick={() => setAsideView(ASIDE_VIEWS.CASH_WITHDRAWAL_EXPLANATION)}>
                  What is the cash withdrawal?
                </a>
              </small>
            }
            {unit_trust_recipient_id !== 'default-retirement-unit-trust' && unit_trust_recipient_id !== 'cash-withdrawal' &&
              <Box width='50%'>
                <ToggleFieldWithLabel
                  name={`${name}.payload.unit_trust.show_preview`}
                  label={'Preview unit trust'}
                />
                <Hider withTransition collapse
                  show={get(values, `${name}.payload.unit_trust.show_preview`, false)}
                  render={() =>
                    <div className='ml-4 [&>.ui-card]:border [&>.ui-card]:border-gray-300'>
                      <AssetCard
                        asset={selected_unit_trust}
                        supportedCurrencies={supportedCurrencies}
                      />
                    </div>
                  }
                />
              </Box>
            }
            <Box className='m-4'>
              <SelectWithLabel name={`${name}.payload.living_annuity.recipient_id`} label='Living annuity'
                subLabel={'The remaining capital from the asset will go into the Living Annuity'}
                options={getLivingAnuityMaturityOptions(assets)}
              />
            </Box>
            {living_annuity_recipient_id === 'default-retirement-living-annuity' &&
              <small style={{ marginLeft: "1rem" }}>
                <a onClick={() => setAsideView(ASIDE_VIEWS.DEFAULT_LIVING_ANNUITY_EXPLANATION)}>
                  What is the Default Living Annuity?
                </a>
              </small>
            }
            {living_annuity_recipient_id !== 'default-retirement-living-annuity' &&
              <Box width='50%'>
                <ToggleFieldWithLabel
                  name={`${name}.payload.living_annuity.show_preview`}
                  label={'Preview living annuity'}
                />
                <Hider withTransition collapse
                  show={get(values, `${name}.payload.living_annuity.show_preview`, false)}
                  render={() =>
                    <AssetCard
                      asset={selected_living_annuity}
                      supportedCurrencies={supportedCurrencies}
                    />
                  }
                />
              </Box>
            }
          </Box>
        }
      />
    </Box>
  )
}

// Functions

const getSublabelIfOffshore = (formAssetType) => {
  if (formAssetType === 'offshore_investment') {
    return 'The interest rate field should equal the risk profile return in the offshore currency plus the expected local currency depreciation.'
  }
}

const interestRateControlOptions = (formAssetType) => {
  const baseOptions = [
    {
      name: 'Use overall risk profile',
      value: INTEREST_RATE_CONTROL.BASED_ON_RISK_PROFILE
    },
    {
      name: 'Specify a return rate',
      value: INTEREST_RATE_CONTROL.BASED_ON_SPECIFIED_INTEREST_RATE
    }
  ]

  if (['money_market', 'other_income'].includes(formAssetType)) {
    return [{
      name: 'Specify a return rate',
      value: INTEREST_RATE_CONTROL.BASED_ON_SPECIFIED_INTEREST_RATE
    }]
  }

  if (formAssetType === 'retirement_annuity') {
    return retirementAnnuityOptions(baseOptions)
  } else {
    return [...baseOptions, {
      name: 'Specify an asset allocation',
      value: INTEREST_RATE_CONTROL.BASED_ON_ASSET_ALLOCATION
    }]
  }
}

const retirementAnnuityOptions = (baseOptions) => {
  return [...baseOptions,
  {
    name: 'Specify an asset allocation',
    value: INTEREST_RATE_CONTROL.BASED_ON_ASSET_ALLOCATION
  }
  ]
}

const getLivingAnuityMaturityOptions = (assets) => {
  const existingLivingAnnuities = assets.map((asset) =>
    asset.id && asset.asset_type === 'living_annuity' && {
      name: asset.name,
      value: asset.id
    }
  ).filter(Boolean)

  return [{
    name: `Default Living Annuity`,
    value: 'default-retirement-living-annuity'
  }, ...existingLivingAnnuities]
}

const getUnitTrustMaturityOptions = (assets) => {
  const existingUnitTrusts = assets.map((asset) =>
    asset.id && asset.asset_type === 'unit_trust' && {
      name: asset.name,
      value: asset.id
    }
  ).filter(Boolean)

  return [{
    name: `Cash withdrawal`,
    value: 'cash-withdrawal'
  }, {
    name: 'Default Unit Trust',
    value: 'default-retirement-unit-trust'
  }, ...existingUnitTrusts]
}

const eventAgeSubtext = (age, currentAge, retirementAge) =>
  `Year: ${getYearFromAge(age, currentAge)}` +
  (age < retirementAge ? `. The asset will only mature on or after the retirement age (${retirementAge}) set on the plan` : '')
